// ============================================================================
// EXTEND STAY MODAL
// ============================================================================
// Phase-1 Stay Extension flow for a CHECKED_IN booking. Lets the front desk
// push the check-out date forward, surfaces a live "Extra charge" preview,
// blocks save when any of the three availability layers reports a conflict,
// and optionally collects payment inline.
//
// Status stays CHECKED_IN — this is a date/total change, not a lifecycle
// transition.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { stayExtensionService } from 'api/services/stayExtensionService';
import type { Booking } from 'api/types/booking.types';
import type { ExtendStayPreview, RoomConflict } from 'api/types/stayExtension.types';
import notificationService from 'services/notificationService';
import { SyutSelect } from 'components/ui';

interface ExtendStayModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onExtended: () => void;
}

const PAYMENT_MODE_OPTIONS = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Card', label: 'Credit / Debit Card' },
  { value: 'UPI', label: 'UPI' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
  { value: 'Company Credit', label: 'Company Credit' },
];

const formatCurrency = (n: number) => `₹${(n ?? 0).toFixed(2)}`;
const formatDate = (iso?: string) => (iso ? iso.slice(0, 10) : '—');

const addDays = (yyyyMmDd: string, days: number): string => {
  const d = new Date(yyyyMmDd);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const ExtendStayModal: React.FC<ExtendStayModalProps> = ({
  isOpen, booking, onClose, onExtended,
}) => {
  const currentCheckOutIso = booking?.checkOutDate?.slice(0, 10) ?? '';
  const minNewDate = currentCheckOutIso ? addDays(currentCheckOutIso, 1) : '';

  // --- Form state ---
  const [newCheckOutDate, setNewCheckOutDate] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [collectNow, setCollectNow] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<string>('0');
  const [paymentMode, setPaymentMode] = useState<string>('');
  const [paymentReference, setPaymentReference] = useState('');

  // --- Preview + submission state ---
  const [preview, setPreview] = useState<ExtendStayPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  // Reset whenever the modal opens for a new booking.
  useEffect(() => {
    if (!isOpen || !booking) return;
    setNewCheckOutDate(minNewDate); // default = current + 1 night
    setNotes('');
    setCollectNow(false);
    setPaymentAmount('0');
    setPaymentMode('');
    setPaymentReference('');
    setPreview(null);
    setPreviewError(null);
    setTouched(false);
  }, [isOpen, booking, minNewDate]);

  // --- Debounced preview fetch on date change -------------------------------
  const previewSeqRef = useRef(0);
  useEffect(() => {
    if (!isOpen || !booking || !newCheckOutDate) {
      setPreview(null);
      return;
    }
    if (newCheckOutDate <= currentCheckOutIso) {
      setPreview(null);
      setPreviewError('New check-out must be after the current check-out date.');
      return;
    }

    const seq = ++previewSeqRef.current;
    setPreviewLoading(true);
    setPreviewError(null);
    const timer = setTimeout(async () => {
      try {
        const result = await stayExtensionService.getPreview(booking.bookingId, newCheckOutDate);
        if (seq !== previewSeqRef.current) return; // stale
        setPreview(result);
      } catch (err: any) {
        if (seq !== previewSeqRef.current) return;
        setPreviewError(err?.response?.data?.message || err?.message || 'Failed to compute preview.');
      } finally {
        if (seq === previewSeqRef.current) setPreviewLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [isOpen, booking, newCheckOutDate, currentCheckOutIso]);

  // Pre-fill payment amount with the extra charge when the operator opens
  // the collapsible. They can override.
  useEffect(() => {
    if (collectNow && preview && (!paymentAmount || paymentAmount === '0')) {
      setPaymentAmount(preview.additionalTotal.toFixed(2));
    }
  }, [collectNow, preview, paymentAmount]);

  const parsedPaymentAmount = Number.parseFloat(paymentAmount || '0');
  const conflicts: RoomConflict[] = preview?.conflicts ?? [];
  const hasConflicts = conflicts.length > 0;

  const dateError = useMemo(() => {
    if (!newCheckOutDate) return 'New check-out date is required.';
    if (newCheckOutDate <= currentCheckOutIso) return 'New check-out must be after the current check-out date.';
    return '';
  }, [newCheckOutDate, currentCheckOutIso]);

  const paymentAmountError = useMemo(() => {
    if (!collectNow) return '';
    if (Number.isNaN(parsedPaymentAmount) || parsedPaymentAmount <= 0)
      return 'Enter a payment amount greater than 0.';
    return '';
  }, [collectNow, parsedPaymentAmount]);

  const paymentModeError = useMemo(() => {
    if (!collectNow) return '';
    if (!paymentMode.trim()) return 'Select a payment mode.';
    return '';
  }, [collectNow, paymentMode]);

  const hasFormErrors = !!(dateError || paymentAmountError || paymentModeError);
  const canSubmit = !submitting && !previewLoading && !hasConflicts && !hasFormErrors && !!preview;

  if (!isOpen || !booking) return null;

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await stayExtensionService.extend(booking.bookingId, {
        newCheckOutDate,
        notes: notes.trim() || undefined,
        collectNow,
        paymentAmount: collectNow ? parsedPaymentAmount : undefined,
        paymentMode: collectNow ? paymentMode : undefined,
        paymentReference: collectNow && paymentReference.trim() ? paymentReference.trim() : undefined,
      });
      notificationService.toast.success(
        `Stay extended for booking "${booking.bookingNumber}" to ${newCheckOutDate}.`,
      );
      onExtended();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.errors?.[0]
        || err?.message
        || 'Failed to extend stay.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={submitting ? undefined : onClose} />
      <div className="modal-container modal-md">
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Extending stay…
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Extend Stay</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Stay summary */}
          <div style={{
            padding: '0.75rem 0.875rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            marginBottom: '0.875rem',
            fontSize: '0.85rem',
          }}>
            <div><strong>Booking:</strong> {booking.bookingNumber}</div>
            <div><strong>Guest:</strong> {booking.guestFullName || '—'}</div>
            <div><strong>Status:</strong> {booking.bookingStatus || booking.bookingStatusCode}</div>
            <div><strong>Check-In:</strong> {formatDate(booking.checkInDate)}</div>
            <div><strong>Current Check-Out:</strong> {formatDate(booking.checkOutDate)}</div>
            <div><strong>Current Total:</strong> {formatCurrency(booking.finalGrandTotal ?? 0)}</div>
            <div><strong>Advance Received:</strong> {formatCurrency(booking.advanceReceived ?? 0)}</div>
            <div><strong>Balance Due:</strong> {formatCurrency(booking.balanceDue ?? 0)}</div>
          </div>

          {/* New check-out date */}
          <div className="form-group" data-field="newCheckOutDate">
            <label className="form-label">
              New Check-Out Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className={`form-input${touched && dateError ? ' form-field-error' : ''}`}
              value={newCheckOutDate}
              min={minNewDate}
              onChange={(e) => setNewCheckOutDate(e.target.value)}
              onBlur={() => setTouched(true)}
              disabled={submitting}
            />
            {touched && dateError && <div className="form-error-message">{dateError}</div>}
          </div>

          {/* Preview panel */}
          {previewLoading && (
            <div style={{
              padding: '0.625rem 0.875rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              marginTop: '0.5rem',
              fontSize: '0.8125rem',
              color: 'var(--text-muted)',
            }}>
              Computing preview…
            </div>
          )}

          {!previewLoading && previewError && (
            <div className="form-error-message" style={{ marginTop: '0.5rem' }}>
              {previewError}
            </div>
          )}

          {!previewLoading && preview && !hasConflicts && (
            <div style={{
              padding: '0.75rem 0.875rem',
              background: 'rgba(34, 197, 94, 0.08)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '6px',
              marginTop: '0.5rem',
              fontSize: '0.85rem',
            }}>
              <div style={{ marginBottom: '0.25rem' }}>
                <strong>Additional nights:</strong> {preview.additionalNights}
              </div>
              {/* Priced at the stay's agreed figure — its adjustments and discount carry over (6b). */}
              <div><strong>Agreed rate per night (before tax):</strong> {formatCurrency(preview.agreedNightlyAmount)}</div>
              <div><strong>Added nights before tax:</strong> {formatCurrency(preview.additionalSubtotal)}</div>
              <div><strong>Tax on the added nights:</strong> {formatCurrency(preview.additionalTax)}</div>
              <div style={{
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: '1px dashed var(--border-color)',
                fontWeight: 600,
              }}>
                Extra charge total: {formatCurrency(preview.additionalTotal)}
              </div>
              <div style={{ marginTop: '0.25rem' }}>
                <strong>New total:</strong> {formatCurrency(preview.newFinalGrandTotal)}
                {' — '}
                <strong>New balance due:</strong> {formatCurrency(preview.newBalanceDue)}
              </div>
            </div>
          )}

          {!previewLoading && hasConflicts && (
            <div style={{
              padding: '0.75rem 0.875rem',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              marginTop: '0.5rem',
              fontSize: '0.85rem',
              color: 'var(--text-primary)',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '0.375rem', color: '#dc2626' }}>
                Cannot extend — {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''}:
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8125rem' }}>
                {conflicts.map((c, i) => (
                  <li key={i} style={{ marginBottom: '0.125rem' }}>{c.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Notes */}
          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label className="form-label">Notes (optional)</label>
            <textarea
              className="form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="e.g. Guest requested 2 extra nights."
              style={{ resize: 'vertical', minHeight: '52px' }}
              disabled={submitting}
            />
          </div>

          {/* Collect now — collapsible */}
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            background: 'var(--bg-primary)',
          }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontSize: '0.85rem', fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer',
            }}>
              <input
                type="checkbox"
                checked={collectNow}
                onChange={(e) => setCollectNow(e.target.checked)}
                disabled={submitting}
              />
              Collect payment now
            </label>

            {collectNow && (
              <div className="form-grid-3" style={{ marginTop: '0.5rem' }}>
                <div className="form-group" data-field="paymentAmount">
                  <label className="form-label">Amount (₹) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={`form-input${touched && paymentAmountError ? ' form-field-error' : ''}`}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    disabled={submitting}
                  />
                  {touched && paymentAmountError && (
                    <div className="form-error-message">{paymentAmountError}</div>
                  )}
                </div>
                <div className="form-group" data-field="paymentMode">
                  <SyutSelect
                    label={<>Mode <span className="text-danger">*</span></>}
                    value={paymentMode}
                    onChange={(v) => setPaymentMode((v as string) || '')}
                    options={PAYMENT_MODE_OPTIONS}
                    isClearable
                    error={touched ? paymentModeError : undefined}
                    size="sm"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Reference</label>
                  <input
                    type="text"
                    maxLength={100}
                    className="form-input"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="Txn id / receipt no."
                    disabled={submitting}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            title={hasConflicts ? 'Resolve conflicts to enable.' : undefined}
          >
            Extend Stay
          </button>
        </div>
      </div>
    </>
  );
};

export default ExtendStayModal;
